"use client";

import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { Language, DEFAULT_LANGUAGE, isValidLanguage, parseLanguage, t as translate } from '../../lib/translations';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language | null;
}

/**
 * Get language from URL query parameter
 * @returns Language code or null if not found
 */
function getLanguageFromURL(): Language | null {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  
  if (langParam && isValidLanguage(langParam)) {
    return langParam;
  }
  
  return null;
}

/**
 * Get language from NEXT_LOCALE cookie (client-side fallback for non-HttpOnly cookies)
 * Parses ISO 639-1 codes: en-US, fr-*, es-*, ar-* (all Arabic), ja-* (Japanese)
 * @returns Language code or null if not found
 */
function getLanguageFromCookie(): Language | null {
  if (typeof document === 'undefined') return null;
  
  // Parse cookies to find NEXT_LOCALE (only works for non-HttpOnly cookies)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'NEXT_LOCALE' && value) {
      const decodedValue = decodeURIComponent(value);
      return parseLanguage(decodedValue);
    }
  }
  
  return null;
}

/**
 * Get language from browser settings
 * @returns Language code based on browser language
 */
function getLanguageFromBrowser(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  return parseLanguage(browserLang);
}

/**
 * Detect language with priority: URL param > Cookie > Browser language > Default
 * @param serverLang - Language from server-side cookie reading (for HttpOnly cookies)
 * @returns Detected language
 */
function detectLanguage(serverLang?: Language | null): Language {
  // Priority 1: URL query parameter
  const urlLang = getLanguageFromURL();
  if (urlLang) return urlLang;
  
  // Priority 2: Server-provided language (from HttpOnly NEXT_LOCALE cookie)
  if (serverLang) return serverLang;
  
  // Priority 3: Client-side cookie (fallback for non-HttpOnly cookies)
  const cookieLang = getLanguageFromCookie();
  if (cookieLang) return cookieLang;
  
  // Priority 4: Browser language
  const browserLang = getLanguageFromBrowser();
  if (browserLang !== DEFAULT_LANGUAGE) return browserLang;
  
  // Priority 5: Default language
  return DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children, initialLanguage }: LanguageProviderProps) {
  // Use server-provided language as initial state to avoid hydration mismatch
  const [language, setLanguage] = useState<Language>(initialLanguage || DEFAULT_LANGUAGE);
  const [isClient, setIsClient] = useState(false);

  // Detect language on client side (allows URL param override)
  useEffect(() => {
    setIsClient(true);
    const detectedLang = detectLanguage(initialLanguage);
    if (detectedLang !== language) {
      setLanguage(detectedLang);
    }
  }, [initialLanguage]);

  // Listen for URL changes (for SPAs with react-router)
  useEffect(() => {
    if (!isClient) return;

    const handleURLChange = () => {
      const urlLang = getLanguageFromURL();
      if (urlLang && urlLang !== language) {
        setLanguage(urlLang);
      }
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleURLChange);

    return () => {
      window.removeEventListener('popstate', handleURLChange);
    };
  }, [isClient, language]);

  // Memoize the translation function
  const t = useMemo(() => {
    return (key: string) => translate(key, language);
  }, [language]);

  // Check if current language is RTL (Arabic)
  const isRTL = language === 'ar';

  // Update document direction when language changes
  useEffect(() => {
    if (!isClient) return;
    
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [isClient, isRTL, language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    isRTL,
  }), [language, t, isRTL]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 * @returns LanguageContextValue
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}

/**
 * Hook to get translation function only (convenience hook)
 * @returns Translation function
 */
export function useTranslation() {
  const { t, language, isRTL } = useLanguage();
  return { t, language, isRTL };
}

